import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactElement } from "react";
import { pageKeys } from "../controls/page.ts";
import { classes } from "../../ui/kit/classes.ts";
import { isTextField } from "../../ui/input/text-field.ts";
import { ArtIcon, type IconArt } from "../../ui/kit/ArtIcon.tsx";
import { Glyph, KeyGlyph, logical } from "../../ui/kit/index.ts";
import { iconArt } from "../icons/art.ts";
import { useIcons } from "../icons/context.tsx";
import type { HudArea, SlotRect } from "../hud/slots.ts";
import { emoteKeyLabels, type KeyShown, type LiveKeys, type LiveKeysSource } from "./keys.ts";
import { emojiBox, emoteWheelLayout, entryBoxes, hubText, type HubText, type WheelLayout } from "./layout.ts";
import { PAGE_MARKS } from "./marks.ts";
import { nearestToward, paintRing, sectorAt, stepAround } from "./ring.ts";
import type { WheelEntry, WheelPage } from "./rules.ts";
import type { EmoteWheelSnapshot, EmoteWheelSource } from "./store.ts";
import { EMOTE_WORDING } from "./words.ts";

/**
 * The emote wheel (P2.19-T8, P2.19-T9, P2.19-T11; ADR-114 decisions 1.3 to 1.6 and its notes "the wheel made round"
 * and "the Emoji page as built"; the design's `emotes` card, `ui_kits/surfaces/EmoteWheelSurfaces.jsx`): a ring of
 * sectors drawn pixel by pixel round a hub, in the HUD's layer with no scrim. Entry 1 stands straight up and the rest
 * clockwise, an action's word on its sector, an emoji's art from the page's icon atlas in a cell there, or its name
 * until the atlas has decoded. The hub holds the page's name and the selected entry's word, which assistive technology reads out as it
 * changes. Above the ring a crown holds a marker a page with the current one lit, and the key glyphs of the page
 * before and after at its ends; under it, or beside it on a narrow area, a plaque holds Send and Close with their keys.
 *
 * A key glyph says what its key does now and nothing else: it is drawn in the disabled look, greyed and never struck
 * through, while the wheel's layer is not the topmost (a window opened over it) and while a text field owns the
 * keyboard, the plaque then saying so, and a page key the key table has given to a fighting action is not drawn at
 * all, the page then turning by the other key and the markers.
 *
 * It decides nothing: every control asks the store, and the server judges what is sent. A pointer press on it leaves
 * the keyboard's focus where it was, so Enter still sends and the game keeps its keys. A mouse over the ring selects
 * the sector under it by its angle from the centre, the hub and what lies past the ring selecting nothing; a mouse's
 * click on a sector sends it; a touch on a sector only selects it and sends nothing, Send sending it, so a thumb sends
 * nothing by one tap. The entries are options of a listbox for the keyboard and assistive technology and take no
 * pointer: entered by keyboard (the game menu's item chosen by a key) the selected entry has focus, Left and Right
 * step round the ring, Up and Down jump to the entry nearest that way, Home and End go to the first and the last,
 * Enter and Space send, and Tab walks the crown, the list and the plaque. A page turned while the keyboard holds the
 * list leaves it holding the new page's selected entry. While the keyboard holds the list the selected sector's metal
 * is drawn in the focus colour.
 */

/**
 * The wheel as the HUD draws it: the store it reads, whether it was entered by keyboard since last asked, and what
 * its controls ask.
 */
export interface HudEmotes {
  readonly source: EmoteWheelSource;
  /** What the wheel may show of its three keys: live, idle while its layer is not topmost, or not named at all. */
  readonly keys: LiveKeysSource;
  readonly takeEntry?: (() => boolean) | undefined;
  readonly onTurn: (step: 1 | -1) => void;
  readonly onGoTo: (page: number) => void;
  readonly onSelect: (entry: number) => void;
  readonly onSend: () => void;
  readonly onClose: () => void;
}

/**
 * Keeps a pointer press on the wheel from taking focus from the game.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * A rectangle of the wheel's box as inline CSS of whole logical pixels.
 *
 * @param rect The rectangle, from the box's corner.
 * @returns Its place and size.
 */
function placed(rect: SlotRect): { readonly left: string; readonly top: string; readonly width: string; readonly height: string } {
  return { left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) };
}

/**
 * The ring: a canvas of the ring's square at one canvas pixel a logical pixel, scaled by whole pixels, painted only
 * when the page, the selection or the keyboard's hold changes. It takes the wheel's pointer: a mouse over a sector
 * selects it and a mouse's click sends it; a touch selects only. The canvas's place on the page is read as a pointer
 * enters it and kept until the window is resized, so a pointer's moves read none.
 *
 * @param props The layout, how many sectors, the selected one, whether the keyboard holds the list, and the controls.
 * @returns The canvas.
 */
function Ring({ layout, count, selected, keyboard, emotes }: { readonly layout: WheelLayout; readonly count: number; readonly selected: number; readonly keyboard: boolean; readonly emotes: HudEmotes }): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const image = useRef<ImageData | null>(null);
  const mouse = useRef(false);
  const rect = useRef<DOMRect | null>(null);
  const side = 2 * layout.outer;

  useLayoutEffect(() => {
    const context = canvas.current?.getContext("2d") ?? null;
    if (context === null) {
      return;
    }
    if (image.current?.width !== side) {
      image.current = context.createImageData(side, side);
    }
    paintRing(image.current.data, layout.outer, layout.inner, count, selected, keyboard);
    context.putImageData(image.current, 0, 0);
  }, [side, layout.outer, layout.inner, count, selected, keyboard]);

  useEffect(() => {
    rect.current = null;
    const forget = (): void => {
      rect.current = null;
    };
    window.addEventListener("resize", forget);
    return () => window.removeEventListener("resize", forget);
  }, [layout]);

  /**
   * The sector under a pointer, by its angle from the ring's centre.
   *
   * @param event The pointer's event.
   * @param box The canvas's place on the page.
   * @returns The sector, or -1 for the hub and past the ring.
   */
  const sectorUnder = (event: MouseEvent<HTMLCanvasElement>, box: DOMRect): number => {
    if (box.width <= 0) {
      return -1;
    }
    const per = side / box.width;
    return sectorAt((event.clientX - box.left) * per - layout.outer, (event.clientY - box.top) * per - layout.outer, count, layout.inner, layout.outer);
  };

  /**
   * A mouse over a sector selects it, once each time the sector changes.
   *
   * @param event The move.
   */
  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>): void => {
    if (event.pointerType !== "mouse") {
      return;
    }
    rect.current ??= event.currentTarget.getBoundingClientRect();
    const sector = sectorUnder(event, rect.current);
    if (sector >= 0 && sector !== selected) {
      emotes.onSelect(sector);
    }
  };

  /**
   * A click on a sector selects it, and sends it when a mouse made it.
   *
   * @param event The click.
   */
  const onClick = (event: MouseEvent<HTMLCanvasElement>): void => {
    const sector = sectorUnder(event, event.currentTarget.getBoundingClientRect());
    if (sector < 0) {
      return;
    }
    emotes.onSelect(sector);
    if (mouse.current) {
      emotes.onSend();
    }
  };
  return (
    <canvas
      ref={canvas}
      className="ot-emotes__ring"
      data-emotes="ring"
      aria-hidden="true"
      width={side}
      height={side}
      style={placed({ left: layout.centre.x - layout.outer, top: layout.centre.y - layout.outer, width: side, height: side })}
      onPointerEnter={(event) => {
        rect.current = event.currentTarget.getBoundingClientRect();
      }}
      onPointerDown={(event) => {
        mouse.current = event.pointerType === "mouse";
      }}
      onPointerMove={onPointerMove}
      onClick={onClick}
    />
  );
}

/**
 * One entry of the page, an option of the list that takes no pointer: an action's word on its sector, or an emoji's
 * art centred in its cell and named for assistive technology, its name standing in the cell while there is no art.
 *
 * @param props The entry, its index, how many the page holds, whether it is selected, its box, an emoji's art or null,
 *   and the controls.
 * @returns The option.
 */
function EntryOption({ entry, index, count, selected, box, art, emotes }: { readonly entry: WheelEntry; readonly index: number; readonly count: number; readonly selected: boolean; readonly box: SlotRect; readonly art: IconArt | null; readonly emotes: HudEmotes }): ReactElement {
  /**
   * The focused entry's keys: Left and Right step round the ring, Up and Down jump to the entry nearest that way,
   * Home and End go to the first and the last, and Enter and Space send. Each is taken here, so it neither walks the
   * character nor opens the chat.
   *
   * @param event The key press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    if (event.key === "ArrowRight") {
      emotes.onSelect(stepAround(index, 1, count));
    } else if (event.key === "ArrowLeft") {
      emotes.onSelect(stepAround(index, -1, count));
    } else if (event.key === "ArrowUp") {
      emotes.onSelect(nearestToward("up", count));
    } else if (event.key === "ArrowDown") {
      emotes.onSelect(nearestToward("down", count));
    } else if (event.key === "Home") {
      emotes.onSelect(0);
    } else if (event.key === "End") {
      emotes.onSelect(count - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      if (!event.repeat) {
        emotes.onSelect(index);
        emotes.onSend();
      }
    } else {
      return;
    }
    event.preventDefault();
  };
  return (
    <button type="button" role="option" aria-selected={selected} aria-label={art === null ? undefined : entry.word} tabIndex={selected ? 0 : -1} className={classes("ot-emotes__entry", selected && "is-selected")} data-emotes="entry" data-emote={`${entry.kind}-${entry.id}`} data-emote-drawn={entry.key === null ? undefined : art === null ? "name" : "art"} style={placed(box)} onKeyDown={onKeyDown}>
      {art === null ? entry.word : <ArtIcon art={art} room={box.width} at={null} />}
    </button>
  );
}

/**
 * Listens for focus coming to or leaving a text field anywhere on the page.
 *
 * @param listener Called on each change of focus.
 * @returns A function that stops listening.
 */
function watchFocus(listener: () => void): () => void {
  document.addEventListener("focusin", listener);
  document.addEventListener("focusout", listener);
  return () => {
    document.removeEventListener("focusin", listener);
    document.removeEventListener("focusout", listener);
  };
}

/**
 * Whether a text field, the chat field among them, owns the keyboard now.
 *
 * @returns True while one has focus.
 */
function typingNow(): boolean {
  return isTextField(document.activeElement);
}

/**
 * Whether a text field owns the keyboard, as the page renders without a document.
 *
 * @returns False.
 */
function typingNever(): boolean {
  return false;
}

/**
 * A page key as a control at its end of the crown: the key's glyph in a button that turns the page, drawn idle while
 * the key is the game's or a text field owns the keyboard, and not drawn when the layer does not name the key.
 *
 * @param props Which way it turns, its key's label, how its key is shown, whether a text field owns the keyboard, where
 *   it stands and the wheel's controls.
 * @returns The control, or null for a key the layer does not name.
 */
function TurnKey({ way, legend, shown, busy, box, emotes }: { readonly way: "before" | "after"; readonly legend: string; readonly shown: KeyShown; readonly busy: boolean; readonly box: SlotRect | null; readonly emotes: HudEmotes }): ReactElement | null {
  if (shown === "none" || box === null) {
    return null;
  }
  return (
    <button type="button" className={classes("ot-emotes__turn", (shown === "idle" || busy) && "is-idle")} data-emotes={way} data-emotes-key={busy ? "busy" : shown} aria-label={way === "before" ? EMOTE_WORDING.pageBefore : EMOTE_WORDING.pageAfter} style={placed(box)} onClick={() => emotes.onTurn(way === "before" ? -1 : 1)}>
      <KeyGlyph>{legend}</KeyGlyph>
    </button>
  );
}

/**
 * What separates the legends in their one snapshot string, a character no key label holds.
 */
const LEGEND_SEPARATOR = "\u0000";

/**
 * The wheel's legends as one string, so a change of the key table or the Keyboard layout re-renders the wheel
 * (P1.9-T174): the page keys' and the send key's labels by the page table's layout, and the wheel's own key's label.
 *
 * @returns The four labels, joined.
 */
function readLegends(): string {
  const labels = emoteKeyLabels(pageKeys.layout);
  return [labels.before, labels.after, labels.send, pageKeys.label("emotes")].join(LEGEND_SEPARATOR);
}

/**
 * The crown: a marker a page, the current one lit, each a button to its page, with the page keys at its ends.
 *
 * @param props The pages, the current page, the layout, what is shown of the page keys and their labels, whether a
 *   text field owns the keyboard and the controls.
 * @returns The crown's controls.
 */
function Crown({ pages, current, layout, keys, legends, busy, emotes }: { readonly pages: readonly WheelPage[]; readonly current: number; readonly layout: WheelLayout; readonly keys: LiveKeys; readonly legends: { readonly before: string; readonly after: string }; readonly busy: boolean; readonly emotes: HudEmotes }): ReactElement {
  return (
    <div className="ot-emotes__crown" data-emotes="crown">
      <TurnKey way="before" legend={legends.before} shown={keys.before} busy={busy} box={layout.before} emotes={emotes} />
      {pages.map((page, index) => {
        const box = layout.markers[index];
        return box === undefined ? null : (
          <button key={page.name} type="button" className={classes("ot-emotes__marker", index === current && "is-current")} data-emotes="marker" aria-label={page.name} aria-current={index === current ? "page" : undefined} style={placed(box)} onClick={() => emotes.onGoTo(index)}>
            <Glyph name={PAGE_MARKS[page.name]} />
          </button>
        );
      })}
      <TurnKey way="after" legend={legends.after} shown={keys.after} busy={busy} box={layout.after} emotes={emotes} />
    </div>
  );
}

/**
 * The hub: the page's name and the selected entry's word, large when it is short and fits, which assistive
 * technology reads out as it changes (the author, 2026-10-08: "the name is enough").
 *
 * @param props The page, the selected entry and where its text stands (`hubText`).
 * @returns The hub's text.
 */
function Hub({ page, entry, text }: { readonly page: WheelPage; readonly entry: WheelEntry; readonly text: HubText }): ReactElement {
  return (
    <div className="ot-emotes__hub" data-emotes="hub" style={placed(text.box)}>
      <span className="ot-emotes__page" data-emotes="page">
        {page.name}
      </span>
      <span className={classes("ot-emotes__word", text.large && "is-large")} data-emotes="selected" role="status" aria-live="polite" style={{ maxWidth: logical(text.width) }}>
        {entry.word}
      </span>
    </div>
  );
}

/**
 * One hint of the plaque: a button holding a key glyph and its word, the glyph idle while its key is the game's or a
 * text field owns the keyboard.
 *
 * @param props The hook it is found by, the key's legend, whether the key is idle, whether a text field owns the
 *   keyboard, the word and what a press asks.
 * @returns The hint.
 */
function Hint({ hook, legend, idle, busy, word, onPress }: { readonly hook: "send" | "close"; readonly legend: string; readonly idle: boolean; readonly busy: boolean; readonly word: string; readonly onPress: () => void }): ReactElement {
  return (
    <span className="ot-emotes__hint" data-emotes={hook}>
      <button type="button" className={classes("ot-emotes__press", (idle || busy) && "is-idle")} data-emotes-key={busy ? "busy" : idle ? "idle" : "live"} onClick={onPress}>
        <KeyGlyph>{legend}</KeyGlyph>
        <span className="ot-emotes__press-word">{word}</span>
      </button>
    </span>
  );
}

/**
 * The open wheel.
 *
 * @param props The HUD's area, the wheel's snapshot and controls, and the target frame as drawn (null for none).
 * @returns The wheel.
 */
function OpenWheel({ area, shown, emotes, target }: { readonly area: HudArea; readonly shown: EmoteWheelSnapshot; readonly emotes: HudEmotes; readonly target: SlotRect | null }): ReactElement | null {
  const host = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const held = useRef(false);
  const [keyboard, setKeyboard] = useState(false);
  const keys = useSyncExternalStore(emotes.keys.subscribe, emotes.keys.getSnapshot, emotes.keys.getSnapshot);
  const busy = useSyncExternalStore(watchFocus, typingNow, typingNever);
  const icons = useIcons();
  const cell = emojiBox(icons.snapshot.scale);
  const page = shown.pages[shown.page];
  const selected = page?.entries[shown.entry];
  const legends = useSyncExternalStore(pageKeys.subscribe, readLegends, readLegends);
  const [beforeLabel, afterLabel, send, close] = legends.split(LEGEND_SEPARATOR) as [string, string, string, string];
  const before = keys.before === "none" ? null : beforeLabel;
  const after = keys.after === "none" ? null : afterLabel;
  const targetLeft = target?.left ?? 0;
  const targetTop = target?.top ?? 0;
  const targetWidth = target?.width ?? 0;
  const targetHeight = target?.height ?? 0;
  const targeted = target !== null;
  const layout = useMemo(() => {
    const frame = targeted ? { left: targetLeft, top: targetTop, width: targetWidth, height: targetHeight } : null;
    return emoteWheelLayout({ width: area.width, height: area.height }, shown.pages.length, { before, after, send, close }, { send: EMOTE_WORDING.send, close: EMOTE_WORDING.close, typing: EMOTE_WORDING.typing }, frame);
  }, [area.width, area.height, shown.pages.length, before, after, send, close, targeted, targetLeft, targetTop, targetWidth, targetHeight]);
  const boxes = useMemo(() => (page === undefined ? [] : entryBoxes(layout, page.entries, cell)), [layout, page, cell]);
  const texts = useMemo(() => (page === undefined ? [] : page.entries.map((entry) => hubText(layout, page.name, entry.word))), [layout, page]);

  useLayoutEffect(() => {
    const box = host.current;
    const opener = document.activeElement;
    return () => {
      if (box !== null && box.contains(document.activeElement)) {
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected && !box.contains(opener)) {
          opener.focus();
        } else {
          (document.activeElement as HTMLElement).blur();
        }
      }
    };
  }, []);

  useLayoutEffect(() => {
    const option = list.current?.querySelector<HTMLElement>("[aria-selected='true']") ?? null;
    if (option === null) {
      return;
    }
    const active = document.activeElement;
    const dropped = held.current && (active === null || active === document.body);
    if (emotes.takeEntry?.() === true || list.current?.contains(active) === true || dropped) {
      option.focus();
    }
  }, [shown, emotes]);

  const text = texts[shown.entry];
  if (page === undefined || selected === undefined || text === undefined) {
    return null;
  }
  const idle = keys.send === "idle";

  /**
   * Notes whether the keyboard holds the list, which draws the selected sector's metal in the focus colour.
   */
  const followFocus = (): void => {
    setKeyboard(list.current?.contains(document.activeElement) === true);
  };

  /**
   * Focus coming into the list: the keyboard holds it until focus is taken somewhere else.
   */
  const onListFocus = (): void => {
    held.current = true;
    followFocus();
  };

  /**
   * Focus leaving an option. An option a page turn unmounted takes the focus with it to the body, and the list keeps
   * its hold so the new page's selected option takes the focus back (`dropped` above); focus moved by the player to
   * anything else ends the hold.
   *
   * @param event The blur, whose target is the option left.
   */
  const onListBlur = (event: FocusEvent<HTMLElement>): void => {
    const left = event.target;
    queueMicrotask(() => {
      if (left.isConnected) {
        held.current = list.current?.contains(document.activeElement) === true;
      }
      followFocus();
    });
  };
  return (
    <div ref={host} className="ot-emotes" data-emotes="wheel" role="group" aria-label={EMOTE_WORDING.wheel} style={placed(layout.box)} onMouseDown={keepFocus}>
      <Ring layout={layout} count={page.entries.length} selected={shown.entry} keyboard={keyboard} emotes={emotes} />
      <div ref={list} className="ot-emotes__list" data-emotes="list" role="listbox" aria-label={page.name} onFocus={onListFocus} onBlur={onListBlur}>
        {page.entries.map((entry, index) => {
          const box = boxes[index];
          const art = entry.key === null ? null : iconArt(icons.snapshot, entry.key, `emoji ${entry.word}`, cell, icons.log);
          return box === undefined ? null : <EntryOption key={`${entry.kind}-${entry.id}`} entry={entry} index={index} count={page.entries.length} selected={index === shown.entry} box={box} art={art} emotes={emotes} />;
        })}
      </div>
      <Hub page={page} entry={selected} text={text} />
      <Crown pages={shown.pages} current={shown.page} layout={layout} keys={keys} legends={{ before: beforeLabel, after: afterLabel }} busy={busy} emotes={emotes} />
      <div className={classes("ot-emotes__plaque", layout.beside && "is-beside")} data-emotes="footer" style={placed(busy ? layout.plaqueBusy : layout.plaque)}>
        <Hint hook="send" legend={send} idle={idle} busy={busy} word={EMOTE_WORDING.send} onPress={emotes.onSend} />
        <Hint hook="close" legend={close} idle={idle} busy={busy} word={EMOTE_WORDING.close} onPress={emotes.onClose} />
        {busy ? (
          <span className="ot-emotes__typing" data-emotes="typing">
            {EMOTE_WORDING.typing}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The emote wheel in the HUD's layer, drawn while its store says it is open.
 *
 * @param props The HUD's `area`, the wheel (`emotes`) and the target frame as drawn (`target`, `targetFrameRect`; null
 *   while none is shown), which the crown keeps clear of.
 * @returns The wheel, or null while it is closed.
 * @example
 * <EmoteWheel area={layout} emotes={emotes} target={null} />
 */
export function EmoteWheel({ area, emotes, target }: { readonly area: HudArea; readonly emotes: HudEmotes; readonly target: SlotRect | null }): ReactElement | null {
  const shown = useSyncExternalStore(emotes.source.subscribe, emotes.source.getSnapshot, emotes.source.getSnapshot);
  return shown.open ? <OpenWheel area={area} shown={shown} emotes={emotes} target={target} /> : null;
}
